import {
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Box,
  useTheme,
  alpha,
} from "@mui/material";
import { useMemo, useState } from "react";
import BookingCard from "./BookingCard";
import AddIcon from "@mui/icons-material/Add";
import { useOutletContext } from "react-router-dom";

export default function Bookings({
  lead,
  type,
  bookingConfigs,
  addBooking,
  updateBooking,
  deleteBooking,
}) {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const { globalBookedSlots } = useOutletContext();
  const [activeId, setActiveId] = useState(null);
  const [draftBooking, setDraftBooking] = useState(null);

  // Colors consistent with Reports_v2
  const colors = {
    bgPrimary: "#F8FAFC",
    bgCard: "#FFFFFF",
    bgHover: "#F1F5F9",
    textPrimary: "#0F172A",
    textSecondary: "#64748B",
    textMuted: "#94A3B8",
    border: "#E2E8F0",
    borderLight: "#F1F5F9",
  };

  const config = bookingConfigs?.[type] || {};
  const meta = config.meta || {};

  const durations = meta.durations || [];
  const workingHours = meta.workingHours;
  const label = config.label;

  const slotEndToMinutes = (slot) => {
    if (!slot) return 0;
    const [, end] = slot.split("–").map((v) => v.trim());
    const [h, m] = end.split(":").map(Number);
    return h * 60 + m;
  };

  const bookings = useMemo(() => {
    return (lead?.bookings || [])
      .filter((b) => b.type === type)
      .sort((a, b) => {
        const da = new Date(a.date).getTime();
        const db = new Date(b.date).getTime();
        if (da !== db) return db - da;
        return slotEndToMinutes(b.slot) - slotEndToMinutes(a.slot);
      });
  }, [lead, type]);

  const handleAddClick = () => {
    const tempId = "temp-" + Date.now();
    setDraftBooking({
      _id: tempId,
      type,
      date: "",
      duration: durations[0] || "",
      slot: "",
      status: "Scheduled",
      isNew: true,
    });
    setActiveId(tempId);
  };

  return (
    <Card
      sx={{
        borderRadius: 3,
        bgcolor: colors.bgCard,
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.06)",
        border: `1px solid ${colors.borderLight}`,
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2.5,
            pb: 2,
            borderBottom: `1px solid ${colors.borderLight}`,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 700,
                color: colors.textPrimary,
                textTransform: "capitalize",
              }}
            >
              {config.label || type}
            </Typography>
            <Typography
              sx={{
                fontSize: 12,
                color: colors.textSecondary,
                mt: 0.25,
              }}
            >
              Manage appointment bookings
            </Typography>
          </Box>

          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            onClick={handleAddClick}
            sx={{
              px: 2.5,
              py: 1,
              fontWeight: 600,
              fontSize: 12,
              borderRadius: 2,
              textTransform: "uppercase",
              bgcolor: primaryColor,
              boxShadow: "none",
              "&:hover": {
                bgcolor: primaryColor,
                opacity: 0.9,
                boxShadow: "none",
              },
            }}
          >
            Add New
          </Button>
        </Box>

        {/* Booking List */}
        <Stack spacing={2}>
          {draftBooking && (
            <BookingCard
              booking={draftBooking}
              leadName={lead?.name}
              type={type}
              durations={durations}
              workingHours={workingHours}
              globalBookedSlots={globalBookedSlots}
              open={activeId === draftBooking._id}
              onToggle={() => {
                setActiveId(null);
                setDraftBooking(null);
              }}
              onUpdate={(payload) => {
                addBooking(lead._id, type, payload);
                setDraftBooking(null);
                setActiveId(null);
              }}
              onDelete={() => {
                setDraftBooking(null);
                setActiveId(null);
              }}
            />
          )}

          {!draftBooking && bookings.length === 0 && (
            <Box
              sx={{
                py: 6,
                borderRadius: 2.5,
                bgcolor: colors.bgPrimary,
                border: `1px dashed ${colors.border}`,
                textAlign: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: colors.textPrimary,
                  textTransform: "capitalize",
                  mb: 0.5,
                }}
              >
                No {label?.toLowerCase() || type} added yet
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  color: colors.textMuted,
                }}
              >
                Click the Add New button to create your first booking
              </Typography>
            </Box>
          )}

          {bookings.map((b) => (
            <BookingCard
              key={b._id}
              booking={b}
              leadName={lead?.name}
              type={type}
              durations={durations}
              workingHours={workingHours}
              globalBookedSlots={globalBookedSlots}
              open={activeId === b._id}
              onToggle={() => setActiveId((v) => (v === b._id ? null : b._id))}
              onUpdate={(payload) =>
                updateBooking(lead._id, type, b._id, payload)
              }
              onDelete={() => deleteBooking(lead._id, type, b._id)}
            />
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}
